import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box, Card, CardContent, Grid, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, Tooltip, Typography,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { format } from 'date-fns'
import _ from 'lodash'
import RefreshIcon from '@mui/icons-material/Refresh'
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import HourglassBottomOutlinedIcon from '@mui/icons-material/HourglassBottomOutlined'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined'
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined'
import { useSnackbar } from 'notistack'
import PageTitle from '../../components/PageTitle'
import Loader from '../../common/Loader'
import CopyIcon from '../../common/CopyIcon'
import { FieldLabel, FilterField, FilterDateField } from '../../common/FilterBar'
import { FormProvider, RHFDateRangePickerField, RHFSearchableSelectField, RHFSelectField, RHFTextField } from '../../common/hook-form'
import { useGetAllActiveMerchantsIdAndNameCachedQuery, useGetAllCourierPartnersCachedQuery } from '../../redux/commonApiCalls'
import useTabParam from '../../hooks/useTabParam'
import KpiCard from '../AppointmentDeliveriesV2/shared/KpiCard'
import PillFilters from '../AppointmentDeliveriesV2/shared/PillFilters'
import PageBanner from '../AppointmentDeliveriesV2/shared/PageBanner'
import { wdList, wdSummary, wdExportAll, serverMessage } from './api'
import { fmtKg, fmtInr, fmtDate, ageLabel, deadlineLabel, toQueueCsv, excessWeightKg, WD_TABS, WD_STATUS_OPTIONS, SHIPMENT_TYPE_OPTIONS, DEFAULT_SHIPMENT_TYPE, shipmentTypeParam } from './format'
import { WdStatusChip, HoldChip, ToneChip } from './shared/Chips'
import DisputeDrawer from './DisputeDrawer'
import BulkActionDialog from './BulkActionDialog'

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'urgency', label: 'Deadline soonest' },
  { value: 'excess', label: 'Highest excess' },
]
const URGENCY_OPTIONS = [
  { value: '', label: 'Any deadline' },
  { value: 'crit', label: 'Due within 24h' },
  { value: 'warn', label: 'Due in 24–72h' },
  { value: 'expiring48', label: 'Due within 48h' },
]
const STATUS_OPTIONS = [{ value: '', label: 'Any status in this tab' }].concat(WD_STATUS_OPTIONS)
const PAGE_SIZES = [10, 25, 50, 100]

const toYmd = (d) => (d ? format(new Date(d), 'yyyy-MM-dd') : '')

// The BOM makes Excel read the file as UTF-8, so names and the ₹ in remarks survive.
const downloadCsv = (name, text) => {
  const url = URL.createObjectURL(new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const headSx = { fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#888', fontWeight: 700, whiteSpace: 'nowrap', py: 1 }
const cellSx = { fontSize: 12, py: 1, verticalAlign: 'top' }
const muted = { fontSize: 11, color: '#6b7280' }

/**
 * The Prozo dispute queue — every merchant's weight discrepancies in one list,
 * defaulting to the ones waiting on Prozo. Reads come from /api/v2/wd (the same
 * service the merchant panel uses, so the two sides always agree on a row);
 * decisions go through the drawer to the v1 decision endpoint.
 */
export default function DisputesPage() {
  const [tab, setTab] = useTabParam('dispute')
  const [filters, setFilters] = useState({ shipmentType: DEFAULT_SHIPMENT_TYPE })
  const [sort, setSort] = useState('newest')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(25)
  const [rows, setRows] = useState([])
  const [count, setCount] = useState(0)
  const [tabCounts, setTabCounts] = useState({})
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(false)
  const [reload, setReload] = useState(0)
  const [openRow, setOpenRow] = useState(null)
  const [bulkOpen, setBulkOpen] = useState(false)

  const methods = useForm({ defaultValues: { merchantId: '', courier: '', status: '', q: '', urgency: '', fromDate: '', toDate: '', shipmentType: DEFAULT_SHIPMENT_TYPE } })
  const { handleSubmit, reset } = methods

  const { data: allMerchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const { data: allCouriers = [] } = useGetAllCourierPartnersCachedQuery({
    andfilter: [{ field: 'is_deleted', operator: 'eq', value: '0' }],
    offset: 0, limit: 1000, skipCount: true, includes: 'id,name,prozoId,parent',
  })
  const merchantOptions = useMemo(() => _.orderBy(allMerchants, 'name').map((m) => ({ value: m.id, label: m.name })), [allMerchants])
  const merchantNameById = useMemo(() => Object.fromEntries(allMerchants.map((m) => [m.id, m.name])), [allMerchants])
  // A discrepancy records the courier by its parent name ("Delhivery", "XpressBees"), not by the account's
  // prozoId, so the filter offers parents. Filtering by prozoId matched nothing.
  const courierOptions = useMemo(
    () => _.sortBy(_.uniq(allCouriers.map((c) => c.parent || c.name).filter(Boolean)), (n) => n.toLowerCase()).map((n) => ({ value: n, label: n })),
    [allCouriers],
  )
  const noDateRange = !filters.fromDate && !filters.toDate

  // The list — one request per tab/filter/page change. Explicit status narrows within the tab.
  // The tab, filters and sort as the server's list request — shared by the list and the download, so the
  // file always holds exactly the rows the screen is listing (every page of them).
  const queryBody = useMemo(() => ({
    tab,
    merchantId: filters.merchantId || undefined,
    shipmentType: shipmentTypeParam(filters.shipmentType),
    courier: filters.courier || undefined,
    statusIn: filters.status ? [filters.status] : undefined,
    q: filters.q || undefined,
    urgency: filters.urgency || undefined,
    fromDate: filters.fromDate || undefined,
    toDate: filters.toDate || undefined,
    sort,
  }), [tab, filters, sort])

  const [downloading, setDownloading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const [downloadedSoFar, setDownloadedSoFar] = useState(0)
  const onDownload = async () => {
    setDownloading(true)
    setDownloadedSoFar(0)
    try {
      const { rows: data, capped } = await wdExportAll(queryBody, setDownloadedSoFar)
      if (!data.length) {
        enqueueSnackbar('Nothing to download: no disputes match this tab and these filters.', { variant: 'warning' })
        return
      }
      downloadCsv(`weight-disputes-${tab}-${format(new Date(), 'yyyyMMdd-HHmm')}.csv`, toQueueCsv(data, merchantNameById))
      enqueueSnackbar(capped
        ? `Downloaded the first ${data.length.toLocaleString('en-IN')} disputes (the limit). Narrow by merchant or dates for the rest.`
        : `Downloaded ${data.length.toLocaleString('en-IN')} disputes.`, { variant: capped ? 'warning' : 'success' })
    } catch (err) {
      enqueueSnackbar(serverMessage(err, 'Download failed'), { variant: 'error' })
    } finally {
      setDownloading(false)
    }
  }

  useEffect(() => {
    let alive = true
    setLoading(true)
    const body = { ...queryBody, page, size }
    wdList(body)
      .then((res) => {
        if (!alive) return
        setRows((res && res.data) || [])
        setCount((res && res.count) || 0)
        setTabCounts((res && res.tabCounts) || {})
      })
      .catch((err) => { if (alive) { console.error(err); setRows([]); setCount(0) } })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [tab, filters, sort, page, size, reload])

  // The KPI strip follows merchant, date range and shipment type only — tabs and row filters never move it.
  useEffect(() => {
    let alive = true
    wdSummary({ merchantId: filters.merchantId, fromDate: filters.fromDate, toDate: filters.toDate, shipmentType: shipmentTypeParam(filters.shipmentType) })
      .then((s) => { if (alive) setSummary(s) })
      .catch(() => { if (alive) setSummary(null) })
    return () => { alive = false }
  }, [filters.merchantId, filters.fromDate, filters.toDate, filters.shipmentType, reload])

  const onSubmit = (v) => {
    setFilters({
      merchantId: v.merchantId || '',
      courier: v.courier || '',
      status: v.status || '',
      q: (v.q || '').trim(),
      urgency: v.urgency || '',
      fromDate: toYmd(v.fromDate),
      toDate: toYmd(v.toDate),
      shipmentType: v.shipmentType || DEFAULT_SHIPMENT_TYPE,
    })
    setPage(0)
  }
  const onReset = () => {
    reset({ merchantId: '', courier: '', status: '', q: '', urgency: '', fromDate: '', toDate: '', shipmentType: DEFAULT_SHIPMENT_TYPE })
    setFilters({ shipmentType: DEFAULT_SHIPMENT_TYPE })
    setPage(0)
  }
  const changeTab = (k) => { setTab(k); setPage(0) }

  // After a decision the row may have left this tab; reload the list and the KPIs, keep the
  // drawer on the refreshed row so the reviewer sees the result before closing it.
  const onDecided = useCallback((fresh) => {
    setReload((n) => n + 1)
    if (fresh) setOpenRow(fresh); else setOpenRow(null)
  }, [])

  const s = summary || {}
  // Only a closed row can carry a settled weight, so the column shows where closed rows are listed.
  const showSettled = tab === 'resolved' || tab === 'all'
  const merchantOf = (row) => row.merchantName || merchantNameById[row.merchantId] || row.merchantId

  return (
    <>
      <PageTitle>Weight v2 — Dispute Queue</PageTitle>
      {loading && <Loader />}

      <PageBanner variant='danger' icon={<GavelOutlinedIcon />} title='Weight disputes waiting on Prozo, across every merchant'>
        "Needs a decision" lists disputes a merchant has raised (or a courier has answered). Open a row to see the merchant's
        evidence beside the courier's proof and decide: accept on Prozo's account, courier accepted, reject (merchant pays), or raise it with the courier.
        Rows under "With merchants" are still inside the merchant's own window and auto-accept when it runs out; those that did are under "Auto Accepted".
      </PageBanner>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label='Needs a decision' value={s.disputedCount ?? '—'} sub='disputed by merchant or courier' icon={<GavelOutlinedIcon />} color='red' selected={tab === 'dispute'} onClick={() => changeTab('dispute')} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label='With merchants' value={s.atRiskCount ?? '—'} sub={s.atRisk != null ? `${fmtInr(s.atRisk)} at stake` : ''} icon={<HourglassBottomOutlinedIcon />} color='amber' selected={tab === 'action'} onClick={() => changeTab('action')} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label='Expiring in 48h' value={s.expiring48h ?? '—'} sub='auto-accept unless acted on' icon={<TimerOutlinedIcon />} color='purple' highlightValue={Number(s.expiring48h) > 0} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label='Recovered' value={s.recovered != null ? fmtInr(s.recovered) : '—'} sub={s.winRate != null ? `${s.winRate}% of decided disputes` : ''} icon={<SavingsOutlinedIcon />} color='green' />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label='Avg resolution' value={s.avgResolutionDays != null ? `${s.avgResolutionDays}d` : '—'} sub='raise → close' icon={<SpeedOutlinedIcon />} color='blue' />
        </Grid>
      </Grid>

      <Card sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>
              <FilterField>
                <FieldLabel>Merchant</FieldLabel>
                <RHFSearchableSelectField name='merchantId' label='' options={merchantOptions} fullWidth />
              </FilterField>
              <FilterField>
                <FieldLabel>Courier</FieldLabel>
                <RHFSearchableSelectField name='courier' label='' options={courierOptions} fullWidth />
              </FilterField>
              <FilterField>
                <FieldLabel>Shipment type</FieldLabel>
                <RHFSelectField name='shipmentType' label='' options={SHIPMENT_TYPE_OPTIONS} width='100%' size='small' />
              </FilterField>
              <FilterField>
                <FieldLabel>Status</FieldLabel>
                <RHFSelectField name='status' label='' options={STATUS_OPTIONS} width='100%' size='small' displayEmpty />
              </FilterField>
              <FilterField>
                <FieldLabel>Deadline</FieldLabel>
                <RHFSelectField name='urgency' label='' options={URGENCY_OPTIONS} width='100%' size='small' displayEmpty />
              </FilterField>
              <FilterField>
                <FieldLabel>AWB / order ref / SKU</FieldLabel>
                <RHFTextField name='q' label='' placeholder='Search' fullWidth size='small' InputLabelProps={{ shrink: true }} />
              </FilterField>
              <FilterDateField>
                <FieldLabel>Raised between</FieldLabel>
                <RHFDateRangePickerField label='' />
              </FilterDateField>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                <Box component='button' type='submit'
                  sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#1565c0', color: '#fff', '&:hover': { bgcolor: '#0d47a1' } }}>
                  Filter
                </Box>
                <Box component='button' type='button' onClick={onReset}
                  sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#ef4444', color: '#fff', '&:hover': { bgcolor: '#dc2626' } }}>
                  Reset
                </Box>
              </Box>
            </Box>
          </FormProvider>
        </CardContent>
      </Card>

      {noDateRange && (
        <Typography sx={{ ...muted, mb: 1 }}>
          Open disputes are shown in full. Resolved ones, and the Recovered and Avg resolution figures, cover the last 30 days:
          pick "Raised between" dates to look further back. An exact AWB is found at any age.
        </Typography>
      )}
      <Stack direction='row' alignItems='center' justifyContent='space-between' flexWrap='wrap' rowGap={1} sx={{ mb: 0.5 }}>
        <PillFilters pills={WD_TABS} value={tab} onChange={changeTab} counts={tabCounts} />
        <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 2 }}>
          <Typography sx={muted}>Sort</Typography>
          <Box component='select' value={sort} onChange={(e) => { setSort(e.target.value); setPage(0) }}
            sx={{ fontSize: 12, py: 0.5, px: 1, borderRadius: '6px', border: '1px solid #ccc', bgcolor: '#fff' }}>
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Box>
          <Tooltip title='Reload'><IconButton size='small' onClick={() => setReload((n) => n + 1)}><RefreshIcon fontSize='small' /></IconButton></Tooltip>
          <Box component='button' type='button' onClick={onDownload} disabled={downloading}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 12, fontWeight: 600, px: 1.25, py: 0.5, borderRadius: '6px',
              border: '1px solid #ccc', bgcolor: '#fff', color: '#1a1a2e', cursor: downloading ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
            <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />{downloading ? (downloadedSoFar ? `Preparing… ${downloadedSoFar.toLocaleString('en-IN')} rows` : 'Preparing…') : 'Download report'}
          </Box>
          <Box component='button' type='button' onClick={() => setBulkOpen(true)}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 12, fontWeight: 600, px: 1.25, py: 0.5, borderRadius: '6px',
              border: '1px solid #1565c0', bgcolor: '#fff', color: '#1565c0', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <PlaylistAddCheckOutlinedIcon sx={{ fontSize: 16 }} />Bulk action
          </Box>
        </Stack>
      </Stack>

      <Card sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
        <TableContainer>
          <Table size='small' sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>AWB · merchant</TableCell>
                <TableCell sx={headSx}>Status</TableCell>
                <TableCell sx={headSx}>Courier · SKU</TableCell>
                <TableCell sx={headSx} align='right'>Declared</TableCell>
                <TableCell sx={headSx} align='right'>Courier billed</TableCell>
                {showSettled && <TableCell sx={headSx} align='right'>Settled at</TableCell>}
                <TableCell sx={headSx} align='right'>Excess</TableCell>
                <TableCell sx={headSx} align='center'>Evidence</TableCell>
                <TableCell sx={headSx}>Raised · deadline</TableCell>
                <TableCell sx={headSx} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const dl = row.status === 'NEW' ? deadlineLabel(row.deadlineAt) : null
                return (
                  <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpenRow(row)}>
                    <TableCell sx={cellSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>{row.awb}<CopyIcon text={row.awb} /></Box>
                      <Typography sx={muted}>{merchantOf(row)}</Typography>
                      {row.orderId && <Typography sx={{ ...muted, fontSize: 10.5 }}>ref {row.orderId}</Typography>}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Stack spacing={0.5} alignItems='flex-start'>
                        <WdStatusChip status={row.status} />
                        <HoldChip holdStatus={row.holdStatus} holdAmount={row.holdAmount} />
                      </Stack>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{row.courier || '—'}</Typography>
                      <Typography sx={muted} title={row.product || ''}>{row.sku || (row.product ? row.product : '—')}</Typography>
                    </TableCell>
                    <TableCell sx={cellSx} align='right'>
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{fmtKg(row.originalWeight)}</Typography>
                      {row.courierSentKg != null && <Typography sx={muted}>pushed {fmtKg(row.courierSentKg)}</Typography>}
                    </TableCell>
                    <TableCell sx={cellSx} align='right'>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#b91c1c' }}>{fmtKg(row.appliedWeight)}</Typography>
                      {(row.deadKg != null || row.volKg != null) && (
                        <Typography sx={muted}>dead {fmtKg(row.deadKg)} · vol {fmtKg(row.volKg)}</Typography>
                      )}
                    </TableCell>
                    {showSettled && (
                      <TableCell sx={cellSx} align='right'>
                        {row.status === 'SETTLED_PROSHIP' && row.settledKg != null ? (
                          <>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#b45309' }}>{fmtKg(row.settledKg)}</Typography>
                            {row.settledCharge != null && <Typography sx={muted}>{fmtInr(row.settledCharge)} charged</Typography>}
                          </>
                        ) : <Typography sx={muted}>—</Typography>}
                      </TableCell>
                    )}
                    <TableCell sx={cellSx} align='right'>
                      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{fmtInr(row.excessAmount)}</Typography>
                      <Typography sx={muted} title={row.slabDiffKg != null ? 'Courier-billed slab − brand-pushed slab' : 'No slab recorded: raw weight difference'}>+{fmtKg(excessWeightKg(row))}</Typography>
                    </TableCell>
                    <TableCell sx={cellSx} align='center'>
                      <Stack direction='row' spacing={1} justifyContent='center' alignItems='center'>
                        <Tooltip title={`${row.merchantImages || 0} merchant photo(s) · ${row.courierImages || 0} courier proof image(s)`}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontSize: 11.5, color: (row.merchantImages || 0) > 0 ? '#1d4ed8' : '#9ca3af' }}>
                            <ImageOutlinedIcon sx={{ fontSize: 15 }} />{row.merchantImages || 0}/{row.courierImages || 0}
                          </Box>
                        </Tooltip>
                        {row.hasVideo && <Tooltip title='Packing video attached'><VideocamOutlinedIcon sx={{ fontSize: 15, color: '#1d4ed8' }} /></Tooltip>}
                      </Stack>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: 12 }}>{ageLabel(row.createdAt)}</Typography>
                      <Typography sx={{ ...muted, fontSize: 10.5 }}>{fmtDate(row.createdAt)}</Typography>
                      {dl && <Box sx={{ mt: 0.5 }}><ToneChip tone={dl.tone} label={dl.text} /></Box>}
                    </TableCell>
                    <TableCell sx={cellSx} align='right'>
                      <Box component='button' type='button' onClick={(e) => { e.stopPropagation(); setOpenRow(row) }}
                        sx={{ fontSize: 11.5, fontWeight: 700, px: 1.5, py: 0.6, borderRadius: '6px', border: '1px solid #1565c0', color: '#1565c0', bgcolor: '#fff', cursor: 'pointer', whiteSpace: 'nowrap', '&:hover': { bgcolor: '#e3f2fd' } }}>
                        {row.isClosed ? 'View' : 'Review'}
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={showSettled ? 10 : 9} sx={{ textAlign: 'center', py: 5 }}>
                    <ReportProblemOutlinedIcon sx={{ fontSize: 28, color: '#cbd5e1' }} />
                    <Typography sx={{ fontSize: 12.5, color: '#6b7280', mt: 0.5 }}>Nothing in this tab for the current filters.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component='div'
          count={count}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={size}
          onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0) }}
          rowsPerPageOptions={PAGE_SIZES}
          sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': { fontSize: 12 } }}
        />
      </Card>

      <DisputeDrawer
        row={openRow}
        open={!!openRow}
        onClose={() => setOpenRow(null)}
        onDecided={onDecided}
        merchantName={openRow ? merchantOf(openRow) : ''}
      />

      <BulkActionDialog
        open={bulkOpen}
        onClose={(processed) => { setBulkOpen(false); if (processed) setReload((n) => n + 1) }}
      />
    </>
  )
}
